import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { line, fill, POINT,
  Color, previewOf, sheetOf, LIST_PAGES, Size, VISIT_NOTE_MAX_CHARS, VISIT_NOTE_MAX_PHOTOS, VISIT_NO_SIGNATURE_REASON_LABELS, VISIT_STAMP_LABELS, clockInZone, myJobsTapPath, completeVisitProblem, formatDateTime, pendingLinesProblem, previewVisitProblem, hoursLabel, serviceReportPathOf, serviceReportReadOnly, serviceReportStateLabel, t, visitIsFixed, visitLineLabel,
  type ClockInAnswer, type CustomerChoice, type Sheet, type ClockOutAnswer, type CompleteVisitInput, type JobChecklistAnswer, type SendServiceReportAnswer, type ServiceReportBar, type ServiceReportPreviewAnswer, type ServiceReportState, type StatusColorGroup, type TravelAnswer, type VisitNoteRow, type VisitOutcome, type VisitPageAnswer, type VisitTap, type VisitTapAnswer,
} from '@bms2/shared';
import { Button, Checkbox, NumericInput, RadioGroup, Select, TextInput, Textarea } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { DetailList, DetailRow } from '../../components/composites/DetailList';
import { FormFrame } from '../../components/forms';
import { PageModule } from '../../shell/PageModule';
import { AccessDenied } from '../../shell/Fallbacks';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { post, upload, useApi } from './api';
import { BannerPills } from './BannerPills';
import { DocumentHistoryPanel } from './DocumentHistoryPanel';
import { DocumentNotesRail, DocumentViewPage } from './DocumentViewPage';
import { DocumentSheetFrame } from './DocumentSheetFrame';
import { drawFrozenSheet, frozenSheetWaiting, type FrozenSheet } from './frozenSheet';
import { JobChecklist } from './JobChecklist';
import { SignaturePad } from './SignaturePad';
import { PicturePreview, VisitPhotoControl, VisitPhotoGrid, VisitPhotoViewer, type OpenPhoto } from './VisitPhotos';
import { VisitFilesPanel } from './VisitFilesPanel';
import { VisitPhotoAssetWindow, type LinkingPhoto } from './VisitPhotoAsset';
import { openSheet } from './ViewPdfLink';
import { BookVisitModal, EditVisitModal, visitPagePath, visitPageRoute, workOrderVisitsPath } from './WorkOrderVisits';
import { workOrderRecordTimePath } from './WorkOrderLabour';
import { MY_JOBS_CLOCK_IN_PATH, MY_JOBS_CLOCK_OUT_PATH, MY_JOBS_TRAVEL_PATH, type Told } from '../work/myJobsApi';
import { toldNotice } from '../work/ToldNotice';
import docStyles from './DocumentPage.module.css';
import styles from './WorkOrderVisitPage.module.css';

/** 🔒 A report's state as its pill reads it, the colours the Service Reports list's seeded rows carry (decision 2910): open in amber, signed and sent in green, void in grey. */
const REPORT_GROUPS: Readonly<Record<ServiceReportState, StatusColorGroup>> = { Open: 'warning', Signed: 'good', Sent: 'good', Void: 'inactive' };

const TAP_LABEL_KEYS = { OnMyWay: 'myJobs.onMyWay', Arrived: 'myJobs.arrived', Leaving: 'myJobs.leaving' } as const satisfies Record<VisitTap, string>;

/** 🔒 Decision 4320: a picture chosen in a note or photo window, with its own caption, sent when the window saves. */
interface Picture {
  key: string;
  file: File;
  caption: string;
}

/**
 * What the bar's open window holds: typed time, On My Way's minutes, the offered travel, a note with its pictures or a photo
 * alone. 🔒 Decision 4320: a note and a photo are posted under the line they were opened on, '' for none,
 * named in the window and not chosen there; `dropped` says pictures past the limit were left out.
 */
type BarWindow =
  | { kind: 'record'; lineId: string; hours: number | null; note: string }
  | { kind: 'onMyWay'; minutes: number | null; offered: number | null }
  | { kind: 'travel'; minutes: number | null; offered: number | null; typed?: boolean; endsAt?: string }
  | { kind: 'note'; body: string; lineId: string; lineLabel: string | null; pictures: Picture[]; dropped: boolean }
  | { kind: 'photo'; lineId: string; lineLabel: string | null; pictures: Picture[]; opened: number; dropped: boolean };

/**
 * 🔒 The visit's page (piece 29; decisions 1902, 1908, 1910, 2061, 2062;
 * T1263) is its Service Report's page, drawn on the document page frame
 * (decision 3960; T2293): the number and *Service Report* in the band with the
 * report's state pill and the pencil to Edit Visit; the Details view's tiles —
 * The Visit with who went and the trip's four stamps, Lines Worked, Parts
 * Fitted, Checklists and the Completion Sheet — and Notes, Files and History as
 * the rail's tabs; the technician's bar in the frame's footer. Complete the
 * Visit stands for the lead alone on a started visit and opens the sheet: the
 * customer signs, then the lead, and the lead's signature freezes it. Service
 * Report: Print opens the frozen PDF, Send mails it, and Book a Return Visit
 * opens Book Another Visit with this visit's crew.
 */
export function WorkOrderVisitPage({ workOrderId, visitId, apiBaseUrl = '' }: { workOrderId: string; visitId: string; apiBaseUrl?: string }) {
  const zone = useViewerZone();
  const page = useApi<VisitPageAnswer>(visitPagePath(workOrderId, visitId, zone), apiBaseUrl);
  const dateFormat = useDateFormat();
  const [sheet, setSheet] = useState<Sheet | null>(null);
  /** 🔒 The sheet as it opened (T2347): captured once at `openSheetFor`, so a reload under the open sheet never moves what counts as typed. */
  const [sheetOpened, setSheetOpened] = useState<Sheet | null>(null);
  /** 🔒 The owner's 5b: null on the lead's step; on the second, the sheet as it will print, read with the answers then typed. */
  const [sheetRead, setSheetRead] = useState<ServiceReportPreviewAnswer | null>(null);
  const [sending, setSending] = useState<{ ticked: string[]; message: string } | null>(null);
  const [booking, setBooking] = useState<number | null>(null);
  // 🔒 Decision 2813: Edit Visit, keyed to the version it opened on so a reopening reads the visit afresh.
  const [editing, setEditing] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [told, setTold] = useState<string | null>(null);
  // 🔒 Decision 2111: the job's checklist as the band last read it, so the sheet names what stands unanswered.
  const [checklist, setChecklist] = useState<JobChecklistAnswer | null>(null);
  // 🔒 T3054: a read that failed will not answer, so the hold on Complete lifts; the server asks the checklist again at Complete and refuses what stands open, and the checklist's own panel offers a retry.
  const [checklistFailed, setChecklistFailed] = useState(false);
  const checklistRead = useCallback((answer: JobChecklistAnswer) => {
    setChecklistFailed(false);
    setChecklist(answer);
  }, []);
  const checklistUnread = useCallback(() => setChecklistFailed(true), []);
  // 🔒 Decision 2910: the bar's window and what its last press moved, said on the screen as My Jobs says it.
  const [barWindow, setBarWindow] = useState<BarWindow | null>(null);
  const [barTold, setBarTold] = useState<Told | null>(null);
  // 🔒 Decision 4320: the picture open full size, from a card or the feed.
  const [viewing, setViewing] = useState<OpenPhoto | null>(null);
  /** 🔒 Decision 13310: the photo Link to Asset was pressed on, while its window is open. */
  const [linking, setLinking] = useState<LinkingPhoto | null>(null);
  const pictureKeys = useRef(0);
  const answer = page.state.kind === 'ready' ? page.state.data : null;
  /**
   * 🔒 The owner's 4a (decision 3962): a Signed, Sent or Void report opens on its frozen sheet, the PDF Complete the Visit froze at
   * the version it wrote, its pages drawn into the document frame. Keyed to that version's path, so a reload of the page draws
   * nothing again; a sheet that cannot be drawn answers null, and the report opens on Details with a line pointing at Print.
   */
  // 🔒 Decision 13090: the path the answer names, the visit's sheet route for a worker its assignment names and the file route for anyone else.
  const frozenPath = answer ? serviceReportPathOf(answer.visit) : null;
  const [frozen, setFrozen] = useState<{ path: string; drawn: FrozenSheet | null } | null>(null);
  useEffect(() => {
    if (frozenPath === null) return undefined;
    let live = true;
    void drawFrozenSheet(`${apiBaseUrl}${frozenPath}`).then((drawn) => {
      if (live) setFrozen({ path: frozenPath, drawn });
    });
    return () => {
      live = false;
    };
  }, [frozenPath, apiBaseUrl]);
  const frozenNow = frozen !== null && frozen.path === frozenPath ? frozen : null;
  const sheetFailed = frozenPath !== null && frozenNow !== null && frozenNow.drawn === null;
  if (page.denied) return <AccessDenied reason={page.deniedReason} />;
  // 🔒 Decision 2910: the page is the visit's Service Report, titled with its number; an older reply keeps the visit's own title.
  const title = answer ? answer.report?.number ?? fill(t('workOrder.visitPage.title'), { number: answer.workOrderNumber, visit: answer.visit.label }) : t('documentName.serviceReport');
  const readOnly = serviceReportReadOnly(answer?.report?.state ?? null);
  const bar: ServiceReportBar | null = answer?.bar && !readOnly ? answer.bar : null;

  /** One press through a My Jobs route or the report's own, the page read again once it is through. */
  const press = async <T,>(path: string, body: unknown, done: (data: T) => void): Promise<boolean> => {
    setBusy(true);
    setError(null);
    const outcome = await post<T>(path, body, apiBaseUrl);
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return false;
    }
    done(outcome.data);
    page.reload();
    return true;
  };
  const tap = (which: VisitTap) => {
    if (which === 'OnMyWay') {
      setError(null);
      const offered = bar?.driveMinutes && bar.driveMinutes > 0 ? bar.driveMinutes : null;
      setBarWindow({ kind: 'onMyWay', minutes: offered, offered });
    }
    else void press<VisitTapAnswer>(myJobsTapPath(visitId, which), {}, (data) => setBarTold({ kind: 'tap', answer: data }));
  };
  const clockIn = (lineId: string): Promise<boolean> => {
    // 🔒 Decision 12680: the visit not yet started and no tap of the worker's own made, which the bar's offered taps say.
    const offerEmpty = bar?.taps.includes('OnMyWay') === true;
    return press<ClockInAnswer>(MY_JOBS_CLOCK_IN_PATH, { workOrderId, lineId, timeZone: zone }, (data) => {
      // 🔒 Decision 4320: a Start Line on a second line ends the first at the same minute, and the notice says so.
      setBarTold({ kind: 'in', answer: data });
      // 🔒 T1159: the minutes since the worker's own On My Way are offered, never written until confirmed.
      // 🔒 Decision 12680: a first Clock In with no On My Way of the worker's own offers the Travel with no minutes filled.
      // 🔒 T3828: a worker with an On My Way of their own, too young or too old to offer, is not told they made none; the entry ends at this Clock In.
      setBarWindow(data.travelOffer ? { kind: 'travel', minutes: data.travelOffer.minutes, offered: data.travelOffer.minutes, endsAt: data.startedAt } : offerEmpty && !data.onMyWayTapped ? { kind: 'travel', minutes: null, offered: null, typed: true, endsAt: data.startedAt } : null);
    });
  };
  const actOnWindow = async (open: BarWindow) => {
    const wrote = open.kind === 'record'
      ? await press<unknown>(workOrderRecordTimePath(workOrderId, open.lineId), { timeZone: zone, hours: open.hours, note: open.note }, () => setBarTold(null))
      : open.kind === 'onMyWay'
        ? await press<VisitTapAnswer>(myJobsTapPath(visitId, 'OnMyWay'), { minutes: open.minutes }, (data) => setBarTold({ kind: 'tap', answer: data }))
        : open.kind === 'travel'
          ? await press<TravelAnswer>(MY_JOBS_TRAVEL_PATH, { workOrderId, minutes: open.minutes, timeZone: zone, ...(open.endsAt ? { endsAt: open.endsAt } : {}) }, (data) => setBarTold({ kind: 'travel', answer: data }))
          : open.kind === 'note' && open.pictures.length === 0
            ? await press<VisitPageAnswer>(workOrderVisitsPath(workOrderId, visitId, 'notes'), { body: open.body, lineId: open.lineId || null }, () => setTold(t('serviceReport.noteAdded')))
            : await sendPictures({ body: open.kind === 'note' ? open.body : '', lineId: open.lineId, pictures: open.pictures });
    if (wrote) setBarWindow(null);
  };
  /** 🔒 Decision 4320: a note's words and every picture taken with it in one request, each picture's caption beside it in order. */
  const sendPictures = async (open: { body: string; lineId: string; pictures: readonly Picture[] }): Promise<boolean> => {
    if (open.pictures.length === 0) return false;
    const form = new FormData();
    if (open.body.trim()) form.append('body', open.body);
    // 🔒 Decision 4121: the job's line the entry is posted under travels beside its pictures.
    if (open.lineId) form.append('lineId', open.lineId);
    for (const picture of open.pictures) {
      form.append('file', picture.file);
      form.append('caption', picture.caption);
    }
    setBusy(true);
    setError(null);
    const outcome = await upload<VisitPageAnswer>(workOrderVisitsPath(workOrderId, visitId, 'notes/photo'), form, apiBaseUrl);
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return false;
    }
    setTold(t('serviceReport.noteAdded'));
    page.reload();
    return true;
  };
  const windowReady = (open: BarWindow): boolean => {
    if (open.kind === 'record') return open.lineId !== '' && open.note.trim() !== '' && open.hours !== null && open.hours > 0;
    if (open.kind === 'onMyWay' || open.kind === 'travel') return open.minutes !== null && open.minutes >= 1;
    // 🔒 Decision 4320: a note carries words or a picture; a photo alone carries a picture.
    if (open.kind === 'note') return (open.body.trim() !== '' || open.pictures.length > 0) && open.body.length <= VISIT_NOTE_MAX_CHARS && open.pictures.length <= VISIT_NOTE_MAX_PHOTOS;
    return open.pictures.length > 0 && open.pictures.length <= VISIT_NOTE_MAX_PHOTOS;
  };
  const windowTitle = (open: BarWindow): string => ({
    record: t('serviceReport.recordTitle'), onMyWay: t('myJobs.onMyWay'), travel: t('myJobs.travelTitle'), note: t('serviceReport.noteTitle'), photo: t('serviceReport.photoTitle'),
  })[open.kind];
  /**
   * 🔒 What the leave guard reads (decision 3061): any answer a person gave since the window opened. What the bar laid in, the only line, the drive
   * minutes it read or the travel it offered, is not typed.
   */
  const windowTyped = (open: BarWindow): boolean => {
    if (open.kind === 'record') return open.hours !== null || open.note !== '' || open.lineId !== (bar?.lines.length === 1 ? bar.lines[0]!.lineId : '');
    if (open.kind === 'onMyWay' || open.kind === 'travel') return open.minutes !== open.offered;
    // 🔒 Decision 4320: the pictures a card's photo control opened the window with are not typed; a caption, a picture added or one removed is.
    if (open.kind === 'note') return open.body !== '' || open.pictures.length > 0;
    return open.pictures.length !== open.opened || open.pictures.some((picture) => picture.caption !== '');
  };
  /** 🔒 Decision 4320: pictures as the window holds them, up to the limit a note carries; any past it are left out and said to be. */
  const picturesFrom = (files: readonly File[], held: number): { pictures: Picture[]; dropped: boolean } => {
    const room = Math.max(0, VISIT_NOTE_MAX_PHOTOS - held);
    return { pictures: files.slice(0, room).map((file) => ({ key: `picture-${(pictureKeys.current += 1)}`, file, caption: '' })), dropped: files.length > room };
  };
  /** 🔒 Decision 4320: Add Note opens on the line it was pressed under, '' for the visit's own note; the window names it and does not ask it. */
  const openNote = (lineId: string, lineLabel: string | null) => {
    setError(null);
    setBarWindow({ kind: 'note', body: '', lineId, lineLabel, pictures: [], dropped: false });
  };
  /** 🔒 Decision 4320: a card's photo control opens a photo window on its line holding the pictures taken, each to caption. */
  const openPhoto = (lineId: string, lineLabel: string | null, files: readonly File[]) => {
    setError(null);
    const taken = picturesFrom(files, 0);
    setBarWindow({ kind: 'photo', lineId, lineLabel, pictures: taken.pictures, opened: taken.pictures.length, dropped: taken.dropped });
  };

  const when = (iso: string | null) => (iso ? formatDateTime(iso, zone, dateFormat) : null);
  const openSheetFor = (a: VisitPageAnswer) => {
    setError(null);
    const opened = sheetOf(a);
    setSheetOpened(opened);
    setSheet(opened);
    setSheetRead(null);
  };
  const closeSheet = () => {
    setSheet(null);
    setSheetRead(null);
  };
  /** 🔒 The owner's 5b: Show the Sheet reads the sheet as it will print through the one print path, and keeps nothing. */
  const readSheet = async (a: VisitPageAnswer, s: Sheet) => {
    setBusy(true);
    const outcome = await post<ServiceReportPreviewAnswer>(serviceReportPreviewPath(visitId), previewOf(a, s), apiBaseUrl);
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    setError(null);
    setSheetRead(outcome.data);
  };
  /** 🔒 Decision 3061: the sheet is typed once any answer differs from the one it opened with, a signature drawn among them. */
  const sheetTyped = (opened: Sheet | null, now: Sheet): boolean => opened !== null && JSON.stringify(now) !== JSON.stringify(opened);
  /** 🔒 Decision 3061: the ticks and the message the window opened with are not typed. */
  const sendTyped = (a: VisitPageAnswer, now: { ticked: string[]; message: string }): boolean => {
    const opened = a.recipients.filter((r) => r.ticked).map((r) => r.personId).sort().join();
    return now.message !== '' || [...now.ticked].sort().join() !== opened;
  };
  const inputOf = (a: VisitPageAnswer, s: Sheet): CompleteVisitInput => ({
    outcome: s.outcome,
    outcomeNote: s.outcome === 'CouldNotComplete' ? s.outcomeNote : null,
    workSummary: s.workSummary,
    customerComment: s.customer === 'NobodyOnSite' ? null : s.customerComment,
    customerSignature: s.customer === 'Signed' && s.customerImage ? { name: s.customerName, image: s.customerImage } : null,
    noSignatureReason: s.customer === 'Signed' ? null : s.customer,
    leadSignature: { name: s.leadName, image: s.leadImage ?? '' },
    lineWork: a.lines.map((row) => ({ lineId: row.lineId, workPerformed: s.lineWork[row.lineId] ?? '' })),
    version: a.visit.version,
  });
  const complete = async (a: VisitPageAnswer, s: Sheet) => {
    setBusy(true);
    const outcome = await post(workOrderVisitsPath(workOrderId, visitId, 'complete'), inputOf(a, s), apiBaseUrl);
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    closeSheet();
    setTold(t('workOrder.visitPage.completed'));
    page.reload();
  };
  const send = async () => {
    if (!sending) return;
    setBusy(true);
    const outcome = await post<SendServiceReportAnswer>(workOrderVisitsPath(workOrderId, visitId, 'send'), { personIds: sending.ticked, message: sending.message || null }, apiBaseUrl);
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    setSending(null);
    setTold(fill(t(outcome.data.sent === 'Sent' ? 'workOrder.visitPage.sent' : 'workOrder.visitPage.sendFailed'), { to: outcome.data.to }));
  };
  // 🔒 The signed version itself, never whichever version is newest (piece 29, the service report), on the path the answer names (decision 13090).
  const print = async (path: string) => {
    const opened = await openSheet(`${apiBaseUrl}${path}`);
    if (opened.outcome !== 'opened') setError(opened.message);
  };

  const runningLine = bar?.lines.find((row) => row.running) ?? null;
  const canNote = answer?.report?.canAddNote === true;
  /** 🔒 End Line and the footer's Clock Out: the viewer's running clock stopped through My Jobs' own route. */
  const clockOut = () => void press<ClockOutAnswer>(MY_JOBS_CLOCK_OUT_PATH, {}, (data) => setBarTold({ kind: 'out', answer: data }));

  return (
    <AsyncStatePanel state={page.state} emptyAction={t('workOrder.visitPage.notFound')} onRetry={page.reload}>
      {(a) => {
        const v = a.visit;
        const state = a.report?.state ?? null;
        // 🔒 Decision 2111 (T889): a visit completed in full waits on every checklist item, each named, until it is answered or signed off.
        const unanswered = (checklist?.items ?? []).filter((item) => item.status === 'Open').map((item) => item.title);
        // 🔒 T1396: a checklist not yet read is unknown and not empty, so Complete is held with a line saying so until the first read lands.
        const waiting = checklist === null && !checklistFailed && sheet?.outcome === 'Completed';
        const checklistStop = waiting ? t('checklist.job.waiting') : sheet && sheet.outcome === 'Completed' && unanswered.length > 0 ? fill(t('checklist.job.unansweredStop'), { items: unanswered.join(', ') }) : null;
        // 🔒 T3851 (piece 29): the sheet's lines still Pending are named beside the unanswered items, and hold the lead's step the same way.
        const pendingStop = sheet && sheet.outcome === 'Completed' ? pendingLinesProblem(a.pendingLines) : null;
        const stop = [pendingStop, checklistStop].filter((sentence): sentence is string => sentence !== null).join(' ') || null;
        const problem = sheet ? completeVisitProblem({ status: v.status, viewerIsLead: a.viewerIsLead }, inputOf(a, sheet)) ?? stop : null;
        // 🔒 The owner's 5b: the lead's step asks what Complete the Visit asks first, and the same checklist stop.
        const workProblem = sheet ? previewVisitProblem({ status: v.status, viewerIsLead: a.viewerIsLead }, previewOf(a, sheet)) ?? stop : null;

        // 🔒 Decision 4320: while the report is Open, Lines Worked carries a card for every line of the job; the sheet keeps `lines`.
        const cards = a.cards ?? a.lines;
        /** A line as its card names it, *Line 2 · Replace the actuator*; a line with no card keeps the bar's own label. */
        const labelOf = (lineId: string): string | null => {
          const row = cards.find((one) => one.lineId === lineId);
          return row ? visitLineLabel(row.lineNumber, row.description) : bar?.lines.find((one) => one.lineId === lineId)?.label ?? null;
        };
        // 🔒 Decision 3960: the footer is the frame's own bar; Edit Visit is the band's pencil, so it is not here.
        // 🔒 Decision 3961 (the owner's 2a): the clock first, then Add Note and Complete the Visit, which stand on a phone; the rest fold under More.
        // 🔒 Decision 4320: every line the viewer can start has its card with Start Line, so the footer's clock is Clock Out alone.
        const clock = bar?.clockRunsHere ? 'out' : null;
        const firstActs = [clock, canNote ? 'note' : null, a.canComplete ? 'complete' : null].filter(Boolean).length;
        // 🔒 One Primary at a time: the clock where it stands, else Complete the Visit; every other act reads quiet beside it.
        const primary = clock ?? (a.canComplete ? 'complete' : null);
        const quiet = primary === null ? {} : { color: Color.Inactive as const };
        // 🔒 Decision 4320: the footer's Add Note posts under the line the viewer's clock runs on, else under no line, and its window says which.
        const footerNote = () => openNote(runningLine?.lineId ?? '', runningLine ? labelOf(runningLine.lineId) : null);
        const footer = (
          <>
            {clock === 'out' ? <span data-report-clock-out="true"><Button size={Size.Medium} color={Color.Warning} text={[t('serviceReport.bar.clockOut')]} disabled={busy} actions={[clockOut]} /></span> : null}
            {canNote ? <span data-report-add-note="true"><Button size={Size.Medium} {...quiet} text={[t('serviceReport.bar.addNote')]} disabled={busy} actions={[footerNote]} /></span> : null}
            {a.canComplete ? <span data-report-complete="true"><Button size={Size.Medium} {...(primary === 'complete' ? { color: Color.Navy } : quiet)} text={[t('workOrder.visitPage.complete')]} disabled={busy} actions={[() => openSheetFor(a)]} /></span> : null}
            {/* 🔒 Decision 2910: the technician's taps, through My Jobs' own routes and rules; they are the viewer's own. */}
            {(bar?.taps ?? []).map((which) => (
              <span key={which} data-report-tap={which}><Button size={Size.Medium} {...quiet} text={[t(TAP_LABEL_KEYS[which])]} disabled={busy} actions={[() => tap(which)]} /></span>
            ))}
            {bar?.canRecord ? <span data-report-record="true"><Button size={Size.Medium} {...quiet} text={[t('serviceReport.bar.recordTime')]} disabled={busy} actions={[() => { setError(null); setBarWindow({ kind: 'record', lineId: bar.lines.length === 1 ? bar.lines[0]!.lineId : '', hours: null, note: '' }); }]} /></span> : null}
            {v.serviceReportFileId ? <span data-report-print="true"><Button size={Size.Medium} {...quiet} text={[t('workOrder.visitPage.print')]} actions={[() => void print(serviceReportPathOf(v)!)]} /></span> : null}
            {a.canSend ? <span data-report-send="true"><Button size={Size.Medium} {...quiet} text={[t('workOrder.visitPage.send')]} actions={[() => { setError(null); setSending({ ticked: a.recipients.filter((r) => r.ticked).map((r) => r.personId), message: '' }); }]} /></span> : null}
            {a.canBookReturn ? <span data-report-return="true"><Button size={Size.Medium} {...quiet} text={[t('workOrder.visitPage.bookReturn')]} actions={[() => setBooking(Date.now())]} /></span> : null}
          </>
        );
        const actions = [
          clock === 'out' ? 'clock-out' : null, canNote ? 'note' : null, a.canComplete ? 'complete' : null,
          ...(bar?.taps ?? []).map((which) => `tap-${which}`), bar?.canRecord ? 'record' : null,
          v.serviceReportFileId ? 'print' : null, a.canSend ? 'send' : null, a.canBookReturn ? 'return' : null,
        ].filter(Boolean).join(' ');

        // 🔒 Piece 29 (the owner's 3a): Notes is the report's feed in the order added, newest last. 🔒 Decision 4320 (the owner's A3a):
        // a note and the pictures taken with it are one entry, who and when, its line where it names one, its words, then its thumbnails.
        const notes = a.report?.notes ?? [];
        const entry = (note: VisitNoteRow, place: 'feed' | 'card') => {
          const at = when(note.createdAt) ?? '';
          return (
            <li key={note.id} className={styles.note} {...(place === 'feed' ? { 'data-report-note': note.id, 'data-report-row': 'note' } : { 'data-visit-line-note': note.id })}>
              <p className={styles.hint}>{fill(t('serviceReport.noteBy'), { name: note.authorName, at })}</p>
              {/* 🔒 Decision 4121: a feed entry names the job's line it was posted under; on its card the line is the card's own. */}
              {place === 'feed' && note.line ? <p className={styles.onLine} data-report-note-line={note.line.lineId}>{note.line.label}</p> : null}
              {note.body ? <p className={styles.account}>{note.body}</p> : null}
              <VisitPhotoGrid photos={note.photos} name={note.authorName} at={at} apiBaseUrl={apiBaseUrl} onOpen={setViewing} />
            </li>
          );
        };
        const notesPanel = (
          <section className={styles.panel} data-report-notes={notes.length}>
            <p className={styles.hint}>{t('serviceReport.notesHint')}</p>
            {notes.length === 0 ? <p className={styles.hint}>{t('serviceReport.noNotes')}</p> : (
              <ul className={styles.notes} data-report-feed={notes.length}>
                {notes.map((note) => entry(note, 'feed'))}
              </ul>
            )}
            {/* 🔒 Decision 4320: Add Note under the list is the visit's own note, posted under no line; a card's acts post under its line. */}
            {canNote ? (
              <div className={styles.feedActions} data-report-feed-actions="true">
                <Button size={Size.Small} color={Color.Inactive} text={[t('serviceReport.feed.addNote')]} disabled={busy} actions={[() => openNote('', null)]} />
              </div>
            ) : null}
          </section>
        );
        // 🔒 Decision 2910: the report's files — its signed sheet and every photo its notes were added with, each the one File row (T3833).
        const filesPanel = (
          <section className={styles.panel} data-report-files="true">
            <VisitFilesPanel
              files={a.files ?? []}
              sheet={v.serviceReportFileId ? { fileId: v.serviceReportFileId, path: serviceReportPathOf(v)! } : null}
              photoIds={notes.flatMap((note) => note.photos.map((photo) => photo.fileId))}
              writes={a.writesJob === true}
              apiBaseUrl={apiBaseUrl}
              onChanged={page.reload}
              onLinkAsset={a.canLinkAsset ? (fileId) => setLinking({ fileId, asset: notes.flatMap((note) => note.photos).find((photo) => photo.fileId === fileId)?.asset ?? null }) : null}
            />
          </section>
        );
        // 🔒 Decision 3960: the visit's own audit rows, the History every document's rail carries.
        const historyPanel = (
          <DocumentHistoryPanel recordType="WorkOrderVisit" recordId={v.id} rows={(a.history ?? []).map((row) => ({ id: row.id, label: row.label, detail: row.detail, at: row.at }))} timeZone={zone} />
        );

        // 🔒 Decision 4320 (the owner's A1a): Lines Worked is the working surface, one card per line, the clock's line active,
        // Start Line or End Line, Add Note and the photo control, then the entries posted under the line, newest last (decision 4121).
        const notesById = new Map(notes.map((note) => [note.id, note]));
        const card = (row: VisitPageAnswer['lines'][number]) => {
          const label = visitLineLabel(row.lineNumber, row.description);
          const own = bar?.lines.find((one) => one.lineId === row.lineId) ?? null;
          const running = own?.running ?? null;
          // 🔒 Start Line on the viewer's own line on the clock's terms; a clock on another line ends there at the same minute.
          const canStart = running === null && own !== null && bar?.lineStart === true;
          const posted = (row.noteIds ?? []).flatMap((id) => {
            const note = notesById.get(id);
            return note ? [note] : [];
          });
          return (
            <li key={row.lineId} className={running ? `${styles.card} ${styles.cardActive}` : styles.card} data-visit-line={row.lineId} data-visit-card-active={running ? 'true' : 'false'}>
              <div className={styles.cardHead}>
                <span className={styles.cardTitle}>{label}</span>
                {row.hours !== null ? <span className={styles.cardHours} data-visit-card-hours={row.hours}>{fill(t('workOrder.visitPage.hours'), { hours: hoursLabel(row.hours) })}</span> : null}
                {/* 🔒 Decision 4320: an Open report's card whose line has no time on this visit says so where the hours stand. */}
                {row.hours === null && !serviceReportReadOnly(state) ? <span className={styles.cardHours} data-visit-card-no-time={row.lineId}>{t('serviceReport.card.noTime')}</span> : null}
              </div>
              {/* 🔒 Decision 5571: the workers the visit sent for this line, beside it. */}
              {row.sentNames !== undefined ? <p className={styles.hint} data-visit-card-sent={row.lineId}>{row.sentNames.length > 0 ? fill(t('serviceReport.card.sent'), { names: row.sentNames.join(', ') }) : t('serviceReport.card.sentNobody')}</p> : null}
              {running ? <p className={styles.cardRunning} data-visit-card-running={row.lineId}>{fill(t('serviceReport.card.running'), { at: clockInZone(running.startedAt, zone) })}</p> : null}
              {row.workPerformed ? <p className={styles.account}>{row.workPerformed}</p> : <p className={styles.hint}>{t('workOrder.visitPage.noWork')}</p>}
              {running || canStart || canNote ? (
                <div className={styles.cardActs} data-visit-card-acts={row.lineId}>
                  {running ? <span data-visit-card-end={row.lineId}><Button size={Size.Small} color={Color.Warning} text={[t('serviceReport.card.endLine')]} disabled={busy} actions={[clockOut]} /></span> : null}
                  {/* 🔒 Decision 12680 (T3821): Start Travel is the worker's own On My Way, beside Start Line until Arrived is tapped. */}
                  {canStart && bar?.taps.includes('OnMyWay') ? <span data-visit-card-travel={row.lineId}><Button size={Size.Small} color={Color.Inactive} text={[t('workOrder.clock.startTravel')]} disabled={busy} actions={[() => tap('OnMyWay')]} /></span> : null}
                  {canStart ? <span data-visit-card-start={row.lineId}><Button size={Size.Small} color={Color.Navy} text={[t('serviceReport.card.startLine')]} disabled={busy} actions={[() => void clockIn(row.lineId)]} /></span> : null}
                  {canNote ? <span data-visit-card-note={row.lineId}><Button size={Size.Small} color={Color.Inactive} text={[t('serviceReport.bar.addNote')]} disabled={busy} actions={[() => openNote(row.lineId, label)]} /></span> : null}
                  {canNote ? <VisitPhotoControl disabled={busy} onPick={(files) => openPhoto(row.lineId, label, files)} attribute="data-visit-card-photo" /> : null}
                </div>
              ) : null}
              {posted.length === 0 ? (canNote ? <p className={styles.hint}>{t('serviceReport.card.noEntries')}</p> : null) : (
                <ul className={styles.notes} data-visit-line-notes={posted.length}>
                  {posted.map((note) => entry(note, 'card'))}
                </ul>
              )}
            </li>
          );
        };

        const details = (
          <>
            {/* 🔒 Decision 3960: The Visit folds in who went, the lead first and marked, and the trip's four stamps, each with who made it. */}
            <section className={docStyles.block} data-report-tile="visit">
              <h2 className={docStyles.heading}>{t('workOrder.visitPage.facts')}</h2>
              <DetailList look="separator">
                <DetailRow title={t('workOrder.visitPage.customer')} detail={a.companyName} />
                <DetailRow title={t('workOrder.visitPage.site')} detail={a.siteName} />
                <DetailRow title={t('workOrder.visitPage.window')} detail={line(when(v.windowStart), v.windowEnd ? `– ${when(v.windowEnd)}` : null)} />
                <DetailRow
                  title={t('workOrder.visitPage.crew')}
                  detail={a.crew.length === 0 ? t('workOrder.visitPage.nobody') : (
                    <span className={styles.crew}>
                      {a.crew.map((person, index) => (
                        <span key={person.employmentId} data-visit-crew={person.employmentId} data-visit-crew-lead={person.isLead ? 'true' : 'false'}>
                          {person.isLead ? line(person.name, POINT, t('workOrder.visitPage.leadMark')) : person.name}{index < a.crew.length - 1 ? ',' : ''}
                        </span>
                      ))}
                    </span>
                  )}
                />
                {a.stamps.map((stamp) => (
                  <DetailRow key={stamp.kind} title={VISIT_STAMP_LABELS[stamp.kind]} detail={stamp.at ? line(when(stamp.at), stamp.by ? fill(t('workOrder.visitPage.stampBy'), { name: stamp.by }) : null) : t('workOrder.visitPage.notYet')} data-visit-stamp={stamp.kind} data-visit-stamp-made={stamp.at ? 'true' : 'false'} />
                ))}
              </DetailList>
            </section>
            <section className={docStyles.block} data-report-tile="lines" data-visit-lines={cards.length}>
              <h2 className={docStyles.heading}>{t('workOrder.visitPage.lines')}</h2>
              <p className={docStyles.muted} data-lines-hint="true">{t('workOrder.visitPage.linesHint')}</p>
              <ul className={styles.cards} data-visit-cards={cards.length}>
                {cards.map(card)}
              </ul>
            </section>
            <section className={docStyles.block} data-report-tile="parts">
              <h2 className={docStyles.heading}>{t('workOrder.visitPage.parts')}</h2>
              <p className={docStyles.muted}>{t('workOrder.visitPage.partsHint')}</p>
              {a.parts.length === 0 ? <p className={docStyles.muted}>{t('workOrder.visitPage.noParts')}</p> : (
                <DetailList look="separator">
                  {a.parts.map((part) => <DetailRow key={part.id} title={part.item} detail={fill(t('workOrder.visitPage.partLine'), { quantity: String(part.quantity), unit: part.unit })} data-visit-part={part.id} />)}
                </DetailList>
              )}
            </section>
            {/* 🔒 Piece 29 (T389): the job's checklist, each item answered the way it asks, before the sheet is signed. */}
            <section className={docStyles.block} data-report-tile="checklists" data-visit-checklist="true">
              <h2 className={docStyles.heading}>{t('serviceReport.checklists')}</h2>
              {/* 🔒 Decision 4320 (the owner's A4a): a photo taken here is posted under the clock's line as well, and the page reads it back. */}
              <JobChecklist workOrderId={a.workOrderId} apiBaseUrl={apiBaseUrl} onRead={checklistRead} onFailed={checklistUnread} {...(readOnly ? {} : { visitId: v.id, onPhoto: page.reload })} />
            </section>
            <section className={docStyles.block} data-report-tile="sheet" data-visit-sheet={v.serviceReportFileId ? 'signed' : 'open'}>
              <h2 className={docStyles.heading}>{t('workOrder.visitPage.sheet')}</h2>
              {v.serviceReportFileId ? (
                <DetailList look="separator">
                  <DetailRow title={t('workOrder.visitPage.outcome')} detail={v.outcome === 'CouldNotComplete' ? line(t('workOrder.visitPage.outcomeCouldNot'), v.outcomeNote ? `${POINT} ${v.outcomeNote}` : null) : t('workOrder.visitPage.outcomeCompleted')} />
                  {v.workSummary ? <DetailRow title={t('workOrder.visitPage.workSummary')} detail={<p className={styles.account}>{v.workSummary}</p>} /> : null}
                  {v.customerComment ? <DetailRow title={t('workOrder.visitPage.comment')} detail={<p className={styles.account}>{v.customerComment}</p>} data-visit-comment="true" /> : null}
                  <DetailRow title={t('workOrder.visitPage.customerSigned')} detail={v.customerSigner ?? (v.noSignatureReason ? VISIT_NO_SIGNATURE_REASON_LABELS[v.noSignatureReason] : '')} data-visit-customer-signature={v.customerSigner ? 'signed' : v.noSignatureReason ?? ''} />
                  <DetailRow title={t('workOrder.visitPage.leadSigned')} detail={v.leadSigner ?? ''} data-visit-lead-signature="true" />
                </DetailList>
              ) : (
                <>
                  <p className={docStyles.muted}>{t('workOrder.visitPage.sheetOpen')}</p>
                  {v.status !== 'Complete' && v.status !== 'Cancelled' && !a.viewerIsLead ? <p className={docStyles.muted} data-visit-not-lead="true">{t('workOrder.visitPage.notLead')}</p> : null}
                  {a.viewerIsLead && (v.status === 'Scheduled' || v.status === 'Dispatched') ? <p className={docStyles.muted} data-visit-not-started="true">{t('workOrder.visitPage.notStarted')}</p> : null}
                </>
              )}
            </section>
            {/* 🔒 Decision 3960: Notes, Files and History are the rail's tabs, standing after the tiles on the Details view as every document's rail does on a phone. */}
            <DocumentNotesRail documentType="ServiceReport" panels={{ notes: notesPanel, files: filesPanel, history: historyPanel }} place="flow" />
          </>
        );

        return (
          <DocumentViewPage
            documentType="ServiceReport"
            icon="serviceReport"
            title={title}
            subtitle={t('documentName.serviceReport')}
            // 🔒 Decision 3960: the state pill in the band, Not Opened Yet before the first stamp.
            status={<BannerPills pills={[{ text: serviceReportStateLabel(state), group: state === null ? 'inactive' : REPORT_GROUPS[state] }]} attribute="data-report-pill" />}
            // 🔒 Decision 3960 (amending 2813): the band's pencil opens Edit Visit, keyed to the version the page read.
            {...(a.canEdit ? { onCorrect: () => { setError(null); setEditing(v.version); } } : {})}
            footer={footer}
            footerAttributes={{ 'data-visit-actions': actions }}
            // 🔒 Decision 3961: on a phone the first acts offered of the clock, Add Note and Complete the Visit stand before More; offered none, the frame's own two.
            footerLeads={firstActs > 0 ? firstActs : 2}
            pageAttributes={{ 'data-visit-page': v.id, 'data-visit-status': v.status, 'data-visit-lead-viewer': a.viewerIsLead ? 'true' : 'false', 'data-report-state': state ?? 'none' }}
            // 🔒 Decision 3960: an open report has no sheet yet, so it opens on Details and offers no Details | PDF choice.
            // 🔒 Decision 3962: a frozen sheet opens the report on it, with Details the switch; while its pages are drawn the frame stands with a line in the paper.
            sheetOffered={frozenPath !== null && !sheetFailed}
            sheet={frozenPath === null || sheetFailed ? null : frozenNow?.drawn?.sheet ?? frozenSheetWaiting(t('serviceReport.sheetDrawing'))}
            layout={frozenNow?.drawn?.layout ?? null}
            pdfPath={sheetFailed ? null : frozenPath}
            ribbon={frozenPath !== null && state !== null ? serviceReportStateLabel(state) : null}
            ribbonGroup={state === null ? null : REPORT_GROUPS[state]}
            listRoute={LIST_PAGES.ServiceReport.route}
            notes={notesPanel}
            files={filesPanel}
            history={historyPanel}
            details={details}
            apiBaseUrl={apiBaseUrl}
            notices={(
              <>
                <Link to={`/work-orders/${a.workOrderId}`} data-visit-job={a.workOrderId}>{fill(t('workOrder.visitPage.backToJob'), { number: a.workOrderNumber })}</Link>
                {/* 🔒 Decision 3100 (T2186): a Fixed visit names itself with its reason, on the red edge the Schedule and the board draw. */}
                {visitIsFixed(v) ? <p className={styles.fixed} data-visit-fixed="true">{fill(t('workOrder.visits.fixedOnPage'), { reason: v.fixedReason ?? '' })}</p> : null}
                {v.status === 'Cancelled' ? <p className={styles.hint} data-visit-cancelled="true">{fill(t('workOrder.visitPage.cancelled'), { reason: v.cancelReason ?? '' })}</p> : null}
                {told ? <p className={styles.good} role="status" data-visit-told="true">{told}</p> : null}
                {error && !sheet && !sending && !barWindow ? <p className={styles.problem} role="alert" data-visit-error="true">{error}</p> : null}
                {barTold ? toldNotice(barTold, zone) : null}
                {runningLine?.running ? <p className={styles.hint} data-report-running={runningLine.lineId}>{fill(t('serviceReport.running'), { line: runningLine.label, at: clockInZone(runningLine.running.startedAt, zone) })}</p> : null}
                {a.report && state === null && v.status !== 'Cancelled' ? <p className={styles.hint} data-report-not-opened="true">{t('serviceReport.notOpenedHint')}</p> : null}
                {sheetFailed ? <p className={styles.hint} data-report-sheet-failed="true">{t('serviceReport.sheetFailed')}</p> : null}
              </>
            )}
          >
            {barWindow ? (
              <FormFrame
                host="window"
                open
                title={windowTitle(barWindow)}
                subtitle={title}
                onCancel={() => setBarWindow(null)}
                cancelLabel={barWindow.kind === 'travel' ? t('myJobs.travelDecline') : t('modal.cancel')}
                dataForm={`report-${barWindow.kind}`}
                typed={windowTyped(barWindow)}
                act={{ label: barWindow.kind === 'onMyWay' ? t('myJobs.onMyWay') : barWindow.kind === 'travel' ? t('myJobs.travelTitle') : t('myJobs.save'), disabled: busy || !windowReady(barWindow), onAct: () => actOnWindow(barWindow) }}
                refusal={error}
                onDismissRefusal={() => setError(null)}
              >
                {barWindow.kind === 'record' && (bar?.lines.length ?? 0) > 1 ? (
                  <Select
                    size={Size.Large}
                    label={t('serviceReport.clockInLine')}
                    value={barWindow.lineId}
                    onChange={(lineId) => setBarWindow({ ...barWindow, lineId })}
                    options={(bar?.lines ?? []).map((row) => ({ value: row.lineId, label: row.label }))}
                    placeholder={t('serviceReport.clockInHint')}
                    dataAttribute={{ 'data-report-line': 'true' }}
                  />
                ) : null}
                {barWindow.kind === 'record' ? (
                  <>
                    <p className={styles.hint}>{t('myJobs.recordHint')}</p>
                    <NumericInput size={Size.Small} kind="number" decimals={2} label={t('myJobs.hours')} value={barWindow.hours} onChange={(hours) => setBarWindow({ ...barWindow, hours })} name="report-record-hours" />
                    <TextInput size={Size.Large} label={t('myJobs.note')} value={barWindow.note} onChange={(note) => setBarWindow({ ...barWindow, note })} name="report-record-note" required />
                  </>
                ) : null}
                {barWindow.kind === 'onMyWay' ? (
                  <>
                    <p className={styles.hint}>{t('myJobs.minutesHint')}</p>
                    <NumericInput size={Size.Small} kind="number" decimals={0} label={t('myJobs.minutes')} value={barWindow.minutes} onChange={(minutes) => setBarWindow({ ...barWindow, minutes })} name="report-on-my-way-minutes" />
                  </>
                ) : null}
                {barWindow.kind === 'travel' ? (
                  <>
                    <p className={styles.hint}>{t(barWindow.typed ? 'workOrder.clock.travelHint' : 'myJobs.travelHint')}</p>
                    <NumericInput size={Size.Small} kind="number" decimals={0} label={t('myJobs.travelMinutes')} value={barWindow.minutes} onChange={(minutes) => setBarWindow({ ...barWindow, minutes })} name="report-travel-minutes" />
                  </>
                ) : null}
                {/* 🔒 Decision 4320: a note or a photo names the line it is posted under; the window does not ask it. */}
                {barWindow.kind === 'note' || barWindow.kind === 'photo' ? (
                  <p className={styles.hint} data-report-post-on={barWindow.lineId || 'visit'}>{barWindow.lineLabel ? fill(t('serviceReport.note.onLine'), { line: barWindow.lineLabel }) : t('serviceReport.note.onVisit')}</p>
                ) : null}
                {barWindow.kind === 'note' ? <Textarea size={Size.Large} label={t('serviceReport.noteBody')} value={barWindow.body} onChange={(body) => setBarWindow({ ...barWindow, body })} name="report-note-body" /> : null}
                {barWindow.kind === 'note' || barWindow.kind === 'photo' ? (
                  <div className={styles.pictures} data-report-pictures={barWindow.pictures.length}>
                    {barWindow.kind === 'note' ? <h3 className={styles.title}>{t('serviceReport.note.pictures')}</h3> : null}
                    {barWindow.pictures.map((picture, index) => (
                      <div key={picture.key} className={styles.picture} data-report-picture={index}>
                        <PicturePreview file={picture.file} />
                        <span className={styles.pictureFields}>
                          <TextInput
                            size={Size.Large}
                            label={fill(t('serviceReport.photo.captionFor'), { name: picture.file.name })}
                            value={picture.caption}
                            onChange={(caption) => setBarWindow({ ...barWindow, pictures: barWindow.pictures.map((one) => (one.key === picture.key ? { ...one, caption } : one)) })}
                            name={`report-picture-caption-${index}`}
                          />
                          <span data-report-picture-remove={index}><Button size={Size.Small} color={Color.Inactive} text={[t('serviceReport.photo.remove')]} actions={[() => setBarWindow({ ...barWindow, pictures: barWindow.pictures.filter((one) => one.key !== picture.key), dropped: false })]} /></span>
                        </span>
                      </div>
                    ))}
                    {barWindow.dropped ? <p className={styles.problem} data-report-pictures-dropped="true">{fill(t('serviceReport.photo.tooMany'), { max: String(VISIT_NOTE_MAX_PHOTOS) })}</p> : null}
                    {/* 🔒 Decision 4320 (the owner's A5a): the shared photo control, camera first on a phone, adds to what the window holds. */}
                    {barWindow.pictures.length < VISIT_NOTE_MAX_PHOTOS ? (
                      <VisitPhotoControl
                        disabled={busy}
                        attribute="data-report-window-photo"
                        onPick={(files) => {
                          const added = picturesFrom(files, barWindow.pictures.length);
                          setBarWindow({ ...barWindow, pictures: [...barWindow.pictures, ...added.pictures], dropped: added.dropped });
                        }}
                      />
                    ) : null}
                  </div>
                ) : null}
              </FormFrame>
            ) : null}

            {sheet ? (
              <FormFrame
                key={v.id}
                host="window"
                open
                width="wide"
                title={t('workOrder.visitPage.completeTitle')}
                subtitle={title}
                onCancel={closeSheet}
                dataForm="visit-complete"
                typed={sheetTyped(sheetOpened, sheet)}
                // 🔒 The owner's 5b: two steps. The lead's step reads the sheet; the second signs beneath it.
                act={sheetRead === null
                  ? { label: t('serviceReport.complete.next'), disabled: busy || workProblem !== null, ...(workProblem ? { disabledReason: workProblem } : {}), onAct: () => readSheet(a, sheet) }
                  : { label: t('workOrder.visitPage.sign'), disabled: busy || problem !== null, ...(problem ? { disabledReason: problem } : {}), onAct: () => complete(a, sheet) }}
                refusal={error}
                onDismissRefusal={() => setError(null)}
              >
                {sheetRead === null ? (
                  <div className={styles.step} data-visit-step="work">
                    <p className={styles.hint}>{t('serviceReport.complete.workHint')}</p>
                    {stop ? <p className={styles.problem} role="alert" data-visit-checklist-stop="true">{stop}</p> : null}
                    <RadioGroup name="visit-outcome" legend={t('workOrder.visitPage.outcome')} orientation="horizontal" value={sheet.outcome} onChange={(outcome) => setSheet({ ...sheet, outcome: outcome as VisitOutcome })} options={[{ value: 'Completed', label: t('workOrder.visitPage.outcomeCompleted') }, { value: 'CouldNotComplete', label: t('workOrder.visitPage.outcomeCouldNot') }]} />
                    {sheet.outcome === 'CouldNotComplete' ? <Textarea size={Size.Large} label={t('workOrder.visitPage.outcomeNote')} value={sheet.outcomeNote} onChange={(outcomeNote) => setSheet({ ...sheet, outcomeNote })} name="visit-outcome-note" required /> : null}
                    <Textarea size={Size.Large} label={t('workOrder.visitPage.workSummary')} value={sheet.workSummary} onChange={(workSummary) => setSheet({ ...sheet, workSummary })} name="visit-work-summary" />
                    {a.lines.map((row) => (
                      <Textarea size={Size.Large} key={row.lineId} label={line(fill(t('workOrder.visitPage.workPerformed'), { line: String(row.lineNumber) }), POINT, row.description)} value={sheet.lineWork[row.lineId] ?? ''} onChange={(text) => setSheet({ ...sheet, lineWork: { ...sheet.lineWork, [row.lineId]: text } })} name={`visit-line-work-${row.lineNumber}`} />
                    ))}
                    {/* 🔒 The customer's comment prints above the customer's signature (decision 1910); the lead takes it down on this step, as the owner's 5b places it. */}
                    <Textarea size={Size.Large} label={t('workOrder.visitPage.comment')} value={sheet.customerComment} onChange={(customerComment) => setSheet({ ...sheet, customerComment })} name="visit-customer-comment" />
                  </div>
                ) : (
                  <>
                    <div className={styles.step} data-visit-step="sheet">
                      {/* 🔒 Back keeps every typed answer and clears both pads: the sheet is read again, and a signature signs the sheet it stood under. */}
                      <span data-visit-back="true"><Button size={Size.Small} color={Color.Inactive} text={[t('serviceReport.complete.back')]} disabled={busy} actions={[() => { setError(null); setSheetRead(null); setSheet({ ...sheet, customerImage: null, leadImage: null }); }]} /></span>
                      <p className={styles.hint}>{t('workOrder.visitPage.completeHint')}</p>
                      {/* 🔒 Piece 29: the customer signs what they are shown, the sheet as it will print with both signature boxes empty, in the sheet frame with zoom and Fit Page. */}
                      {sheetRead.printSheet ? (
                        <div className={styles.preview} data-visit-preview={sheetRead.printSheet.pageCount}>
                          <DocumentSheetFrame sheet={sheetRead.printSheet} layout={sheetRead.printLayout} title={t('serviceReport.complete.previewTitle')} compact flow />
                        </div>
                      ) : <p className={styles.problem} data-visit-preview="none">{t('serviceReport.complete.noPreview')}</p>}
                    </div>
                    <div className={styles.step} data-visit-step="customer">
                      <h3 className={styles.title}>{t('workOrder.visitPage.customerStep')}</h3>
                      <RadioGroup name="visit-customer" legend={t('workOrder.visitPage.customerSigned')} orientation="horizontal" value={sheet.customer} onChange={(customer) => setSheet({ ...sheet, customer: customer as CustomerChoice, customerImage: customer === 'Signed' ? sheet.customerImage : null })} options={[{ value: 'Signed', label: t('workOrder.visitPage.signed') }, { value: 'NobodyOnSite', label: VISIT_NO_SIGNATURE_REASON_LABELS.NobodyOnSite }, { value: 'DeclinedToSign', label: VISIT_NO_SIGNATURE_REASON_LABELS.DeclinedToSign }]} />
                      {/* 🔒 Nobody on site leaves no comment (decision 1910), so one typed on the lead's step is said to be left off. */}
                      {sheet.customer === 'NobodyOnSite' && sheet.customerComment.trim() ? <p className={styles.hint} data-visit-comment-dropped="true">{t('serviceReport.complete.noComment')}</p> : null}
                      {sheet.customer === 'Signed' ? (
                        <>
                          <TextInput size={Size.Large} label={t('workOrder.visitPage.customerName')} value={sheet.customerName} onChange={(customerName) => setSheet({ ...sheet, customerName })} name="visit-customer-name" required />
                          <SignaturePad key="customer" name="customer" label={t('workOrder.visitPage.pad')} onChange={(customerImage) => setSheet((now) => (now ? { ...now, customerImage } : now))} />
                        </>
                      ) : null}
                    </div>
                    <div className={styles.step} data-visit-step="lead">
                      <h3 className={styles.title}>{t('workOrder.visitPage.leadStep')}</h3>
                      <TextInput size={Size.Large} label={t('workOrder.visitPage.leadName')} value={sheet.leadName} onChange={(leadName) => setSheet({ ...sheet, leadName })} name="visit-lead-name" required />
                      <SignaturePad key="lead" name="lead" label={t('workOrder.visitPage.pad')} onChange={(leadImage) => setSheet((now) => (now ? { ...now, leadImage } : now))} />
                    </div>
                  </>
                )}
              </FormFrame>
            ) : null}

            {sending ? (
              <FormFrame
                host="window"
                open
                title={t('workOrder.visitPage.sendTitle')}
                subtitle={title}
                onCancel={() => setSending(null)}
                dataForm="visit-send"
                typed={sendTyped(a, sending)}
                act={{ label: t('workOrder.visitPage.send'), disabled: busy || !a.recipients.some((r) => r.address && sending.ticked.includes(r.personId)), onAct: () => send() }}
                refusal={error}
                onDismissRefusal={() => setError(null)}
              >
                <p className={styles.hint}>{t('workOrder.visitPage.sendHint')}</p>
                {/* 🔒 Decision 2080: the ticks are the Complete rule's recipients, or the interim's where no rule is in force, and the window says which. */}
                <p className={styles.hint} data-visit-recipients-from={a.recipientsFrom}>{t(a.recipientsFrom === 'CompleteRule' ? 'workOrder.visitPage.sendFromRule' : 'workOrder.visitPage.sendNoRule')}</p>
                <h3 className={styles.title}>{t('workOrder.visitPage.recipients')}</h3>
                {a.recipients.length === 0 ? <p className={styles.hint}>{t('workOrder.visitPage.noContacts')}</p> : a.recipients.map((r) => (
                  <span key={r.personId} data-visit-recipient={r.personId}>
                    <Checkbox
                      label={line(r.name, POINT, r.address ?? t('workOrder.visitPage.noAddress'))}
                      checked={sending.ticked.includes(r.personId)}
                      disabled={!r.address}
                      onChange={(on) => setSending({ ...sending, ticked: on ? [...sending.ticked, r.personId] : sending.ticked.filter((id) => id !== r.personId) })}
                      name={`visit-recipient-${r.personId}`}
                    />
                  </span>
                ))}
                <Textarea size={Size.Large} label={t('workOrder.visitPage.message')} value={sending.message} onChange={(message) => setSending({ ...sending, message })} name="visit-send-message" />
              </FormFrame>
            ) : null}

            {viewing ? <VisitPhotoViewer key={viewing.photo.fileId} open={viewing} apiBaseUrl={apiBaseUrl} onClose={() => setViewing(null)} onOriginal={(path) => void print(path)} onLinkAsset={a.canLinkAsset ? () => setLinking({ fileId: viewing.photo.fileId, asset: viewing.photo.asset ?? null }) : null} /> : null}
            {/* 🔨 Mounted only while open and keyed to its photo, so a reopened window never shows another photo's choice. */}
            {linking ? (
              <VisitPhotoAssetWindow
                key={linking.fileId}
                workOrderId={workOrderId}
                visitId={visitId}
                photo={linking}
                choices={a.assetChoices ?? []}
                apiBaseUrl={apiBaseUrl}
                onCancel={() => setLinking(null)}
                onLinked={(_answer, linked) => {
                  setLinking(null);
                  // The opened picture names its new asset at once; the page reads again for every other row.
                  setViewing((open) => (open && open.photo.fileId === linking.fileId ? { ...open, photo: { ...open.photo, asset: linked } } : open));
                  setTold(fill(t('serviceReport.photo.linked'), { name: linked.name }));
                  page.reload();
                }}
              />
            ) : null}

            {booking !== null ? (
              <BookVisitModal
                key={booking}
                workOrderId={a.workOrderId}
                companyId={a.companyId}
                apiBaseUrl={apiBaseUrl}
                title={t('workOrder.visitPage.bookReturn')}
                // 🔒 Pre-filled from this visit: its crew and its lead, the day left to choose (T1263).
                initial={{ startsAt: '', endsAt: '', workers: a.crew.map((person) => ({ id: person.employmentId, label: person.name })), lead: a.crew.find((person) => person.isLead)?.employmentId ?? '' }}
                onClose={() => setBooking(null)}
                onBooked={() => {
                  setBooking(null);
                  setTold(t('workOrder.visitPage.returnBooked'));
                }}
              />
            ) : null}
            {editing !== null ? (
              <EditVisitModal
                key={`${v.id}:${editing}`}
                workOrderId={a.workOrderId}
                companyId={a.companyId}
                apiBaseUrl={apiBaseUrl}
                visit={{ id: v.id, version: v.version, label: v.label, status: v.status, windowStart: v.windowStart, windowEnd: v.windowEnd, crew: a.crew, carriesBooking: v.carriesBooking === true, fixedReason: v.fixedReason ?? null }}
                onClose={() => setEditing(null)}
                onSaved={() => {
                  setEditing(null);
                  setTold(t('workOrder.visits.saved'));
                  page.reload();
                }}
              />
            ) : null}
          </DocumentViewPage>
        );
      }}
    </AsyncStatePanel>
  );
}

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1): where a Service Reports row's visit stands. */
export const serviceReportLocatePath = (visitId: string): string => `/service-reports/${encodeURIComponent(visitId)}`;
/** 🔒 The owner's 5b: the sheet as it will print, read before the pad opens with the lead's unsaved answers in the body. */
export const serviceReportPreviewPath = (visitId: string): string => `${serviceReportLocatePath(visitId)}/preview`;

/**
 * 🔒 A Service Reports list row's open (decision 2910): the visit's job read on
 * the job's own read, then the visit's page under it, the one address a report has.
 */
export function ServiceReportRoute({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const { id = '' } = useParams();
  const located = useApi<{ visitId: string; workOrderId: string }>(serviceReportLocatePath(id), apiBaseUrl);
  if (located.denied) return <AccessDenied reason={located.deniedReason} />;
  if (located.state.kind === 'ready') return <Navigate replace to={visitPageRoute(located.state.data.workOrderId, located.state.data.visitId)} />;
  return (
    <PageModule title={t('documentName.serviceReport')} icon="serviceReport">
      <AsyncStatePanel state={located.state} emptyAction={t('workOrder.visitPage.notFound')} onRetry={located.reload}>
        {() => null}
      </AsyncStatePanel>
    </PageModule>
  );
}

/** The route's own reading of the job and the visit, keyed so a second visit's page never shows the first one's sheet. */
export function WorkOrderVisitRoute({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const { id = '', visitId = '' } = useParams();
  return <WorkOrderVisitPage key={`${id}/${visitId}`} workOrderId={id} visitId={visitId} apiBaseUrl={apiBaseUrl} />;
}
